import React, { useState } from "react";
import { motion } from "framer-motion";
import { Shield, Globe, Smartphone, Heart, Coffee, CreditCard, Check } from "lucide-react";
import SEO from "../components/SEO";
import { useLanguage } from "../context/LanguageContext";
import upiQr from "../assets/ayushjoshiAy_UPI_QR.jpeg"; // Import QR Code

export default function Sustain() {
    const [activeTab, setActiveTab] = useState("global"); // 'global' or 'india'
    const { t } = useLanguage();
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText("joshiayush000@oksbi");
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="min-h-screen bg-spiritual-white dark:bg-gray-950 text-gray-800 dark:text-gray-100 font-body py-20 px-6">
            <SEO 
              title={t('sustain_title')} 
              description={t('sustain_sub')} 
              url="/sustain" 
            />

            <main className="max-w-3xl mx-auto space-y-12">

                {/* Header */}
                <header className="text-center space-y-6">
                    <div className="flex justify-center text-brand-blue mb-4">
                        <Shield className="w-12 h-12 opacity-80" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-display font-medium">{t('sustain_title')}</h1>
                    <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 font-light max-w-2xl mx-auto leading-relaxed">
                        {t('sustain_sub')}
                    </p>
                </header>

                {/* Toggle (Segmented Control) */}
                <div className="flex justify-center">
                    <div className="bg-gray-100 dark:bg-gray-900 p-1 rounded-xl inline-flex shadow-inner">
                        <button
                            onClick={() => setActiveTab("global")}
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === "global"
                                ? "bg-white dark:bg-gray-800 shadow-sm text-brand-blue"
                                : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                }`}
                        >
                            {t('sustain_global_tab')}
                        </button>
                        <button
                            onClick={() => setActiveTab("india")}
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === "india"
                                ? "bg-white dark:bg-gray-800 shadow-sm text-brand-blue"
                                : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                }`}
                        >
                            {t('sustain_india_tab')}
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <div className="min-h-[400px]">

                    {/* GLOBAL TAB (PayPal) */}
                    {activeTab === "global" && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-3xl p-8 md:p-12 shadow-xl shadow-blue-900/5 text-center space-y-8"
                        >
                            <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto text-brand-blue">
                                <Globe className="w-8 h-8" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-semibold mb-2">{t('sustain_guardian_title')}</h2>
                                <p className="text-gray-500 dark:text-gray-400">{t('sustain_guardian_desc')}</p>
                            </div>

                            {/* PayPal Placeholder */}
                            <div className="space-y-4 max-w-xs mx-auto">
                                <a
                                    href="https://paypal.me/Antaryatra"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-3 w-full py-4 bg-[#003087] text-white rounded-xl font-medium hover:opacity-90 transition-opacity shadow-lg"
                                >
                                    {/* PayPal P logo or Globe */}
                                    <Globe className="w-5 h-5" />
                                    <span>{t('sustain_paypal_cta')}</span>
                                </a>
                                <p className="text-xs text-gray-400">
                                    {t('sustain_paypal_trust')}
                                </p>
                            </div>
                        </motion.div>
                    )}

                    {/* INDIA TAB (UPI) */}
                    {activeTab === "india" && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-3xl p-8 md:p-12 shadow-xl shadow-blue-900/5 text-center space-y-8"
                        >
                            <div className="w-16 h-16 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto text-green-600">
                                <Smartphone className="w-8 h-8" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-semibold mb-2">{t('sustain_upi_title')}</h2>
                                <p className="text-gray-500 dark:text-gray-400">{t('sustain_upi_desc')}</p>
                            </div>

                            {/* QR Code Placeholder */}
                            <div className="space-y-6 max-w-xs mx-auto">
                                <div className="aspect-square bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-700 relative overflow-hidden group">
                                    <img src={upiQr} alt="UPI QR Code" className="absolute inset-0 w-full h-full object-cover" />
                                </div>

                                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl flex items-center justify-between border border-gray-100 dark:border-gray-700">
                                    <span className="text-xs text-gray-500 font-mono">joshiayush000@oksbi</span>
                                    <button
                                        onClick={handleCopy}
                                        className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${copied ? "text-green-600" : "text-brand-blue hover:underline"
                                            }`}
                                    >
                                        {copied ? (
                                            <>
                                                <Check className="w-3.5 h-3.5" />
                                                {t('sustain_copied')}
                                            </>
                                        ) : (
                                            t('sustain_copy')
                                        )}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </div>

                <div className="text-center space-y-4">
                    <p className="text-sm text-gray-400 italic">
                        {t('sustain_quote')}
                    </p>
                </div>

            </main>
        </div>
    );
}
